import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import { onIdTokenChanged, signInWithCustomToken } from 'firebase/auth';
import { safeAnalytics } from '../utils/analytics';
import { flushPendingEmailCta } from '../utils/emailCtaCapture';
import { ANALYTICS_EVENTS } from '../constants/analytics';
import {
  attemptAuthPersistenceReload,
  releaseAuthPersistenceReloadClaim,
} from '../utils/authPersistenceRecovery';
import { authService, backendUserFields } from '../services/auth.service';
import type { AuthUser, SsoProvider } from '../services/auth.service';
import { apiService } from '../services/api.service';
import { emailVerifiedForGate } from '../services/emailVerifiedGate';
import { auth } from '../config/firebase';
import { clearAllOnboardingDrafts } from '../pages/onboarding/onboardingStorage';
import { clearPendingInvitationToken } from '../utils/pendingInvitation';
import {
  consumeIntendedAccountSwitch,
  isIntendedAccountSwitch,
  pageUser,
  setPagePrincipal,
  shouldReloadForAccount,
} from '../utils/accountSwitch';
import { DRAFT_STORAGE_PREFIX as ONBOARDING_DRAFT_STORAGE_PREFIX } from '../pages/onboarding/onboardingStepConfig';

// Detect if running inside the Bower native Android app.
// The app appends 'BowerApp/1.0' to the Chrome User-Agent on every page load.
const isBowerApp = typeof navigator !== 'undefined' && navigator.userAgent.includes('BowerApp');

/**
 * Should the listener IGNORE a `null` that arrived before any user was ever
 * seen? Extracted from the listener to keep its cognitive complexity under the
 * lint ceiling, and because the BowerApp short-circuit MUST stay ahead of the
 * web recovery: in the native app the workspace pin is normally present, so
 * calling the recovery first would reload the webview out from under
 * `signInWithCustomToken` mid-sign-in.
 */
function shouldIgnoreFirstFireNull(): boolean {
  // BowerApp: the bridge's own user event is still coming; the 10s fallback in
  // the bridge effect unblocks bootLoading if it never arrives.
  if (isBowerApp) return true;
  return attemptAuthPersistenceReload();
}

/**
 * Has a DIFFERENT account replaced the one this tab is running as?
 *
 * Firebase's web persistence is shared by every tab of a browser profile, so a
 * sign-in in another tab hands this tab's listeners the new account with no
 * sign-out in between. Adopting it would leave everything already on screen,
 * caches and open collaboration sessions included, belonging to the first
 * person while requests go out as the second: an open native spreadsheet then
 * stopped sending edits with no error (staging, 2026-10-19). The caller
 * reloads instead, so the tab starts over as whoever is now signed in.
 *
 * Not BowerApp: the native app holds one account per webview, and a reload
 * there waits on a bridge that may not fire again.
 */
function isAccountSwitch(previous: { uid: string } | null, next: { uid: string } | null): boolean {
  return !isBowerApp && previous !== null && next !== null && previous.uid !== next.uid;
}

// Extend the global window type for the native auth bridges
declare global {
  interface Window {
    /**
     * Called by the Bower Android app with a Firebase **custom** token after
     * page load. Triggers `signInWithCustomToken`, producing a real Firebase
     * JS SDK session (refresh token + IndexedDB persistence). The standard
     * `onAuthStateChanged` listener then hydrates the AuthContext via the
     * existing `/auth/login` round-trip — no special web codepath needed.
     */
    __bowerSignInWithCustomToken?: (customToken: string) => void;
    /**
     * Legacy bridge: called by older Bower Android app versions with a
     * Firebase **ID** token. Populates AuthContext directly via
     * `/auth/login` (no JS-SDK auth state). Kept for one release while
     * users update; remove once telemetry shows no app installs older
     * than the custom-token release.
     * @deprecated Use `__bowerSignInWithCustomToken` instead.
     */
    __bowerNativeAuth?: (idToken: string) => void;
    /**
     * Legacy bridge: called by older Bower Android app versions on token
     * rotation. Unused under the custom-token flow — the JS SDK handles
     * its own rotation after `signInWithCustomToken`.
     * @deprecated Remove once `__bowerNativeAuth` is removed.
     */
    __bowerTokenRefresh?: (idToken: string) => void;
  }
}

/**
 * Authentication context
 * Manages user authentication state across the app
 */

interface AuthContextType {
  user: AuthUser | null;
  /** True only while Firebase/native-session hydration is unresolved. Route gates use this. */
  bootLoading: boolean;
  /** True while an interactive password/signup/sign-out action is in flight. */
  actionLoading: boolean;
  signUp: (
    email: string,
    password: string,
    turnstileToken?: string,
    signupExtras?: {
      signupSignals?: import('@bower/shared').SignupSignals;
      honeypot?: string;
    },
  ) => Promise<void>;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  signInWithProvider: (provider: SsoProvider) => Promise<void>;
  /**
   * Collision recovery for password-less accounts: sign in with the account's
   * verified provider, then link the pending credential from the collision.
   * Resolves with whether the link landed (sign-in succeeds regardless).
   */
  signInWithProviderThenLink: (
    provider: SsoProvider,
    pending: import('firebase/auth').AuthCredential,
  ) => Promise<boolean>;
  signOut: () => Promise<void>;
  getAuthToken: () => Promise<string | null>;
  refreshUser: () => Promise<void>;
  /**
   * Optimistically flip the local user to onboarded (and optionally set the
   * chosen display name) after onboarding completes, so route guards don't
   * bounce back to /onboarding on a stale cache before the next hydration.
   * Used by the wizard v2 flow (OnboardingV2), which also collects the name.
   */
  applyOnboardingCompletion: (patch?: { displayName?: string }) => void;
  // Enhanced methods
  checkEmailMethods: (
    email: string,
  ) => Promise<import('../services/auth.service').EmailCheckResult>;
  sendPasswordReset: (email: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  sendEmailVerification: () => Promise<void>;
  updateUserProfile: (updates: { displayName?: string; photoURL?: string }) => Promise<void>;
  // Legacy methods (deprecated)
  /** @deprecated Use signUp instead */
  createAccountWithPassword: (
    email: string,
    password: string,
    displayName?: string,
  ) => Promise<void>;
  /** @deprecated Use signIn instead */
  signInWithPassword: (email: string, password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * The signed-in user's Firebase uid, or null. Unlike `useAuth`, it does not
 * throw outside an AuthProvider: the chat hooks key their stored session on it
 * and render in trees (and tests) that have no provider, where there is simply
 * no user to store a session for.
 */
export function useAuthUserId(): string | null {
  return useContext(AuthContext)?.user?.uid ?? null;
}

/**
 * The signed-in user's internal id (`User.id`, what `createdById` holds), or
 * null. Like `useAuthUserId`, safe outside an AuthProvider.
 */
export function useAuthInternalUserId(): string | null {
  return useContext(AuthContext)?.user?.internalId ?? null;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [bootLoading, setBootLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const isInitialAuthRef = useRef(true);
  const previousUserRef = useRef<AuthUser | null>(null);
  // Tracks whether the native auth bridge has fired at least once.
  // Prevents premature login redirects before the token is injected.
  const bowerBridgeFiredRef = useRef(false);

  useEffect(() => {
    // A fresh page has run as nobody yet.
    setPagePrincipal(null);
    // Listen to auth state changes
    const unsubscribe = authService.onAuthStateChanged(async (authUser) => {
      // In BowerApp mode: always wait for the native auth bridge before
      // accepting any auth state (including cached web sessions). Prevents
      // a flash of the wrong user if a stale web Firebase session exists.
      if (isBowerApp && !bowerBridgeFiredRef.current) {
        return;
      }

      // BowerApp: ignore null events that arrive before any real user has
      // been seen. These come from two places, neither of which is a real
      // signout:
      //   1. signInWithCustomToken race — the new bridge sets
      //      bowerBridgeFiredRef = true synchronously, then awaits
      //      signInWithCustomToken (~500ms-1s). During the await, Firebase's
      //      initial-fire microtask runs the listener with `null`. Without
      //      this gate, that processes the null event, flips bootLoading=false
      //      while user=null, and AppRouter briefly renders the login page
      //      before the real user event arrives.
      //   2. Legacy __bowerNativeAuth bridge populates React state directly
      //      without ever signing into the JS SDK; the JS SDK is perpetually
      //      null. Processing those null events would clobber the bridge's
      //      setUser/setAuthToken with clearAuth, producing a 401 burst on
      //      WorkspaceContext queries.
      //   3. WEB: Firebase can resolve its persisted session to `null` on a
      //      page load where that session is live (see
      //      attemptAuthPersistenceReload for the incident). Same symptom as
      //      (1), but measured NOT to self-correct, so waiting is useless —
      //      the tab has to re-read persistence, which only a reload does.
      // Real signouts transition from a populated previousUser → null and
      // pass this gate untouched, on every platform.
      //
      // Returning here deliberately skips `setBootLoading(false)` below, and
      // that omission is load-bearing for two OTHER files: App.tsx keeps
      // rendering LoadingFallback (so no route, public ones included, mounts
      // over a session that may still resolve) and WorkspaceContext bails at
      // its own `authLoading` guard instead of clearing the workspace pin this
      // recovery depends on. Do not "tidy" it by flipping bootLoading early.
      if (!authUser && !previousUserRef.current && shouldIgnoreFirstFireNull()) {
        return;
      }

      // Another tab signed in as someone else: start over as them, never
      // carry on with the first person's page. No loop: after the reload
      // there is no previous user. A switch this tab made on purpose finishes
      // with its own full load, which a reload here would race.
      if (isAccountSwitch(previousUserRef.current, authUser) && !consumeIntendedAccountSwitch()) {
        if (authUser && shouldReloadForAccount(authUser.uid)) globalThis.location.reload();
        return;
      }
      // From here this page runs as `authUser`: token readers refuse any
      // other account until it changes again (utils/accountSwitch.ts).
      setPagePrincipal(authUser?.uid ?? null);

      // CRITICAL: set the auth token in apiService BEFORE setUser. setUser
      // triggers a re-render which fires TanStack queries (WorkspaceContext
      // /api/workspaces, /api/users/me) gated on `enabled: !!user`. Those
      // queries run on the same tick as setUser and would go out with no
      // Authorization header if the token isn't installed yet, returning
      // 401. The manual signIn()/signUp() paths apply this same protection
      // explicitly; the listener path serves the BowerApp custom-token flow
      // and any cached-IndexedDB hydration on page load.
      if (authUser) {
        const token = await authService.getAuthToken();
        if (token) {
          apiService.setAuthToken(token);
        }
      }

      setUser(authUser);

      if (authUser) {
        // Persistence resolved to a real user, so hand this tab its one-shot
        // back: a later stall in a long-lived tab deserves the same recovery.
        // The intervening success is what keeps that from being a loop.
        releaseAuthPersistenceReloadClaim();

        // Set Analytics user identification safely.
        // Use internal Prisma UUID as PostHog distinct_id for consistency
        // across frontend, backend, and agent service.
        const analyticsUserId = authUser.internalId || authUser.uid;
        safeAnalytics.setUserId(analyticsUserId);

        // Link the internal ID to the Firebase UID so historical PostHog
        // events (tracked under Firebase UID) merge into the same person.
        // Only fire once per user — alias is idempotent but wastes a network call.
        if (authUser.internalId && authUser.uid !== authUser.internalId) {
          const aliasKey = `ph_alias_${authUser.internalId}`;
          if (!localStorage.getItem(aliasKey)) {
            safeAnalytics.aliasUser(authUser.internalId, authUser.uid);
            localStorage.setItem(aliasKey, '1');
          }
        }

        // Set signup date ONCE — won't overwrite if already set
        safeAnalytics.setUserPropertyOnce('signup_date', new Date().toISOString());

        // Identify user with basic properties
        safeAnalytics.identifyUser({
          user_id: analyticsUserId,
          email: authUser.email || '',
          display_name: authUser.bowerName || authUser.displayName || undefined,
          is_internal: authUser.internalAdmin === true,
        });

        // Replay any utm_source=email landing params now that the person is
        // known. No-op when nothing was stashed.
        flushPendingEmailCta();

        // Mark that initial auth is complete
        if (isInitialAuthRef.current) {
          isInitialAuthRef.current = false;
        }
      } else {
        // User logged out - clear everything including workspace
        apiService.clearAuth();
        localStorage.removeItem('workspaceId');
        localStorage.removeItem('workspaceData');
        localStorage.removeItem('workspaceOwnerId');
        // Only on a real sign-out (a user was here), never the first null of a
        // signed-out visit, which may be an invitee whose deep-link token
        // Login just stored. A seat-cap refusal keeps the token for a retry,
        // unstamped, so the next person here would be shown, and consume, this
        // user's invite (CR-049). Here rather than in signOut() below so the
        // forced sign-out in api.service.ts and other tabs' sign-outs clear it
        // too. The refusal copy's retry, reopening the invite link, carries the
        // token in its URL.
        if (previousUserRef.current) clearPendingInvitationToken();
        safeAnalytics.clearUser();
        isInitialAuthRef.current = true; // Reset for next login
      }

      // Update previous user reference
      previousUserRef.current = authUser;
      setBootLoading(false);
    });

    // Cleanup subscription
    return () => {
      unsubscribe();
    };
  }, []); // Empty dependency array - this should only run once on mount

  // Keep apiService.authToken in sync with Firebase's current ID token.
  //
  // onAuthStateChanged (above) fires only on sign-in / sign-out / page load —
  // it misses every token rotation in between (Firebase's hourly auto-refresh,
  // explicit getIdToken(true) calls, claim changes after currentUser.reload()).
  // Without this listener, a user who verifies email in a second tab — or sits
  // long enough for Firebase to rotate the token after verification — would
  // continue to send the stale token from the original tab, hitting
  // 403 EMAIL_VERIFICATION_REQUIRED on every request until they sign out and
  // back in. onIdTokenChanged is Firebase's canonical hook for this.
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) return;
      // Fires first, before the auth-state listener's hydration round trip,
      // so leave as early as possible; never install the other account's
      // token here. A marked switch is left to the page that made it.
      if (isAccountSwitch(previousUserRef.current, firebaseUser)) {
        if (!isIntendedAccountSwitch() && shouldReloadForAccount(firebaseUser.uid)) {
          globalThis.location.reload();
        }
        return;
      }
      try {
        const token = await firebaseUser.getIdToken();
        apiService.setAuthToken(token);
      } catch (error) {
        // Token fetch can fail transiently (network, IDB). Next request's
        // 401 → attemptTokenRefresh path is the recovery, so we just log.
        console.warn('onIdTokenChanged: failed to install fresh token', error);
      }
    });
    return () => unsubscribe();
  }, []);

  // Register native auth bridges for the Bower Android app.
  //
  // Preferred: __bowerSignInWithCustomToken
  //   App POSTs its Firebase ID token to /auth/exchange-token, gets a custom
  //   token back, calls this bridge. We sign in via the JS SDK so the
  //   resulting session lives in IndexedDB — refresh tokens, ID-token
  //   rotation, and persistence across reloads are all handled by Firebase.
  //   The existing onAuthStateChanged listener hydrates the user via
  //   /auth/login automatically; no separate code path needed.
  //
  // Legacy: __bowerNativeAuth / __bowerTokenRefresh
  //   Older app versions inject the ID token directly. Kept for one
  //   release. Populates React state only — no JS SDK auth state. Remove
  //   when no installs older than the custom-token release remain in the
  //   wild.
  useEffect(() => {
    if (!isBowerApp) return;

    // ── Preferred: custom-token sign-in ────────────────────────────────
    window.__bowerSignInWithCustomToken = async (customToken: string) => {
      delete window.__bowerSignInWithCustomToken; // one-time use per page load
      bowerBridgeFiredRef.current = true;
      try {
        // signInWithCustomToken validates the token signature against
        // Firebase Auth and produces a real JS-SDK-managed session. After
        // this resolves, onAuthStateChanged fires and the existing
        // listener calls /auth/login to hydrate the AuthContext.
        await signInWithCustomToken(auth, customToken);
        // Listener clears `bootLoading`. No further work here.
      } catch (error) {
        console.error('BowerApp custom-token sign-in error:', error);
        setBootLoading(false); // unblock UI; user falls through to login screen
      }
    };

    // ── Legacy: ID-token bridge (deprecated) ───────────────────────────
    window.__bowerTokenRefresh = (idToken: string) => {
      apiService.setAuthToken(idToken);
    };

    window.__bowerNativeAuth = async (idToken: string) => {
      delete window.__bowerNativeAuth; // one-time use per page load
      bowerBridgeFiredRef.current = true;
      try {
        apiService.setAuthToken(idToken);
        const response = await apiService.post('/auth/login', { idToken, manualLogin: true });
        if (
          response.success &&
          response.data !== null &&
          typeof response.data === 'object' &&
          'user' in response.data
        ) {
          const bu = (response.data as any).user as {
            id: string;
            uid: string;
            email: string | null;
            name: string | null;
            onboarded: boolean;
            internalAdmin: boolean;
            lastLoggedInWorkspace: string | null;
          };
          if (typeof bu?.uid !== 'string' || bu.uid.length === 0) {
            throw new Error('BowerApp auth: invalid user shape from /auth/login');
          }
          setUser({
            uid: bu.uid,
            internalId: bu.id || undefined,
            email: bu.email,
            displayName: bu.name,
            emailVerified: true, // verified via native app Firebase Auth
            photoURL: null,
            onboarded: bu.onboarded,
            internalAdmin: bu.internalAdmin === true,
            lastLoggedInWorkspace: bu.lastLoggedInWorkspace,
            bowerName: bu.name,
          });
          const nativeAnalyticsId = bu.id || bu.uid;
          safeAnalytics.setUserId(nativeAnalyticsId);
          if (bu.id && bu.uid !== bu.id) {
            const aliasKey = `ph_alias_${bu.id}`;
            if (!localStorage.getItem(aliasKey)) {
              safeAnalytics.aliasUser(bu.id, bu.uid);
              localStorage.setItem(aliasKey, '1');
            }
          }
          safeAnalytics.identifyUser({
            user_id: nativeAnalyticsId,
            email: bu.email || '',
            is_internal: bu.internalAdmin === true,
          });
        }
      } catch (error) {
        console.error('BowerApp native auth bridge error:', error);
      } finally {
        setBootLoading(false);
      }
    };

    // Safety fallback: if no bridge fires (e.g., user opened the site
    // outside the native app, or the app is unable to reach the auth
    // exchange backend), unblock bootLoading so the standard web login
    // screen takes over rather than spinning forever.
    //
    // Budget rationale: on a slow tablet the native side has to wait
    // for `onPageFinished`, then perform a token-exchange backend
    // round-trip (200–800 ms typical, up to 10 s timeout) before
    // calling this bridge. 10 s leaves comfortable headroom while
    // still failing fast for users who landed here outside the app.
    const fallback = setTimeout(() => {
      if (!bowerBridgeFiredRef.current) {
        delete window.__bowerSignInWithCustomToken;
        delete window.__bowerNativeAuth;
        setBootLoading(false);
      }
    }, 10000);

    return () => {
      clearTimeout(fallback);
      delete window.__bowerSignInWithCustomToken;
      delete window.__bowerNativeAuth;
      delete window.__bowerTokenRefresh;
    };
  }, []);

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      turnstileToken?: string,
      signupExtras?: {
        signupSignals?: import('@bower/shared').SignupSignals;
        honeypot?: string;
      },
    ): Promise<void> => {
      setActionLoading(true);
      try {
        // Reset initial auth flag to ensure workspace clearing
        isInitialAuthRef.current = true;

        // Clear any previous workspace context before auth
        localStorage.removeItem('workspaceId');
        localStorage.removeItem('workspaceData');
        localStorage.removeItem('workspaceOwnerId');

        const authUser = await authService.signUp(
          email,
          password,
          undefined,
          turnstileToken,
          signupExtras,
        );

        // Set the canonical distinct_id (internal Prisma UUID) on the local
        // PostHog instance so any subsequent client-side events in this tab
        // (page views, button clicks, etc.) associate with the correct
        // person, and so feature-flag targeting picks up the user's email
        // domain immediately.
        //
        // The User Signed Up event itself is captured server-side from
        // /auth/signup — see auth.controller.ts. Client capture was
        // unreliable: analytics cookie consent puts PostHog into feature-
        // flags-only mode and silently dropped the trigger event, so the
        // Slack new-user workflow and Welcome Email hog flow never fired
        // for users who declined or never saw the banner.
        const signupAnalyticsId = authUser.internalId || authUser.uid;
        safeAnalytics.setUserId(signupAnalyticsId);
        safeAnalytics.identifyUser({
          user_id: signupAnalyticsId,
          email: authUser.email || email,
          display_name: authUser.bowerName || authUser.displayName || undefined,
          is_internal: authUser.internalAdmin === true,
        });
        // Replay an Email CTA Clicked event under the now-identified user, if
        // they landed via a marketing-email link. No-op when no UTMs were stashed.
        flushPendingEmailCta();

        // CRITICAL: Set auth token BEFORE setting user state
        // This prevents race conditions where useQuery hooks fire before token is available
        const token = await authService.getAuthToken();
        if (token) {
          apiService.setAuthToken(token);
        }

        // Now set user state - this will trigger useQuery hooks that need the token
        setUser(authUser);

        // Auto-send email verification after successful signup
        try {
          await authService.sendEmailVerification();
        } catch (verificationError) {
          // Don't fail signup if email send fails - user can resend later
          console.warn('Failed to send verification email:', verificationError);
        }
      } finally {
        setActionLoading(false);
      }
    },
    [],
  );

  const signIn = useCallback(
    async (email: string, password: string, rememberMe: boolean = false): Promise<void> => {
      setActionLoading(true);
      try {
        // Reset initial auth flag to ensure workspace clearing
        isInitialAuthRef.current = true;

        // Clear any previous workspace context before auth
        localStorage.removeItem('workspaceId');
        localStorage.removeItem('workspaceData');
        localStorage.removeItem('workspaceOwnerId');

        const authUser = await authService.signInWithPassword(email, password, rememberMe);

        // CRITICAL: Set auth token BEFORE setting user state
        // This prevents race conditions where useQuery hooks fire before token is available
        const token = await authService.getAuthToken();
        if (token) {
          apiService.setAuthToken(token);
        }

        // Now set user state - this will trigger useQuery hooks that need the token
        setUser(authUser);

        // Identify under the internal Prisma UUID so subsequent client
        // events land on the canonical PostHog person. The User Signed In
        // event itself is captured server-side from /auth/login when
        // manualLogin=true — see auth.controller.ts. Client capture was
        // gated by analytics cookie consent and silently dropped for
        // every user who declined the banner.
        const signinAnalyticsId = authUser.internalId ?? authUser.uid;
        safeAnalytics.setUserId(signinAnalyticsId);
        safeAnalytics.identifyUser({
          user_id: signinAnalyticsId,
          email: authUser.email || email,
          display_name: authUser.bowerName || authUser.displayName || undefined,
          is_internal: authUser.internalAdmin === true,
        });
      } finally {
        setActionLoading(false);
      }
    },
    [],
  );

  // Shared tail of every SSO sign-in variant: install token BEFORE setUser
  // (useQuery hooks gated on `!!user` must fire with an Authorization header,
  // mirroring signIn), then identify under the internal Prisma UUID. The
  // User Signed Up / SSO Sign In Completed events are captured server-side in
  // auth-sso.controller.ts; this only sets the local distinct_id.
  const completeSsoSignIn = useCallback(async (authUser: AuthUser): Promise<void> => {
    const token = await authService.getAuthToken();
    if (token) apiService.setAuthToken(token);

    setUser(authUser);

    const analyticsId = authUser.internalId ?? authUser.uid;
    safeAnalytics.setUserId(analyticsId);
    safeAnalytics.identifyUser({
      user_id: analyticsId,
      email: authUser.email || '',
      display_name: authUser.bowerName || authUser.displayName || undefined,
      is_internal: authUser.internalAdmin === true,
    });
    flushPendingEmailCta();
  }, []);

  const signInWithProvider = useCallback(
    async (provider: SsoProvider): Promise<void> => {
      // Login owns the interactive popup spinner via `ssoLoading`. Reusing the
      // context's boot/hydration flag here makes AppRouter unmount Login while
      // the popup is open, so collision-recovery state is lost on return.
      setActionLoading(true);
      try {
        isInitialAuthRef.current = true;

        // Clear any previous workspace context before auth
        localStorage.removeItem('workspaceId');
        localStorage.removeItem('workspaceData');
        localStorage.removeItem('workspaceOwnerId');

        const authUser = await authService.signInWithProvider(provider);
        await completeSsoSignIn(authUser);
      } finally {
        setActionLoading(false);
      }
    },
    [completeSsoSignIn],
  );

  const signInWithProviderThenLink = useCallback(
    async (
      provider: SsoProvider,
      pending: import('firebase/auth').AuthCredential,
    ): Promise<boolean> => {
      // Keep the auth route mounted during the recovery popup for the same
      // reason as the initial provider sign-in above.
      setActionLoading(true);
      try {
        isInitialAuthRef.current = true;
        localStorage.removeItem('workspaceId');
        localStorage.removeItem('workspaceData');
        localStorage.removeItem('workspaceOwnerId');

        const { authUser, linked } = await authService.signInWithProviderThenLink(
          provider,
          pending,
        );
        await completeSsoSignIn(authUser);
        return linked;
      } finally {
        setActionLoading(false);
      }
    },
    [completeSsoSignIn],
  );

  // Closes over `user` to attribute the sign-out analytics event, so it depends
  // on it. Not currently used in any effect dependency array (the only memoised
  // identity that must never change for effect-loop safety is refreshUser).
  const signOut = useCallback(async (): Promise<void> => {
    safeAnalytics.track(ANALYTICS_EVENTS.USER_SIGNED_OUT, {});

    setActionLoading(true);
    try {
      await authService.signOut(user?.internalId, user?.uid);
      setUser(null);
      // Clear auth token on logout
      apiService.clearAuth();
      // Clear login form data from sessionStorage (security: don't persist email after sign out)
      sessionStorage.removeItem('loginEmail');
      sessionStorage.removeItem('loginStep');
      // Onboarding drafts hold free-text research answers and a workspace name
      // that is routinely a surname. Completion clears them, but an ABANDONED
      // wizard never reaches completion — and the wizard itself no longer
      // offers a sign-out, so this is the only place a shared machine gets
      // swept. By prefix: the key is auth-derived and sign-out has no current
      // identity to build one from.
      clearAllOnboardingDrafts(ONBOARDING_DRAFT_STORAGE_PREFIX);
    } finally {
      setActionLoading(false);
    }
  }, [user]);

  const getAuthToken = useCallback(async (): Promise<string | null> => {
    return authService.getAuthToken();
  }, []);

  // Enhanced auth methods
  const checkEmailMethods = useCallback(async (email: string) => {
    return authService.checkEmailMethods(email);
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string): Promise<void> => {
    setActionLoading(true);
    try {
      // Reset initial auth flag to ensure workspace clearing
      isInitialAuthRef.current = true;

      // Clear any previous workspace context before auth
      localStorage.removeItem('workspaceId');
      localStorage.removeItem('workspaceData');
      localStorage.removeItem('workspaceOwnerId');

      const authUser = await authService.signInWithPassword(email, password);
      setUser(authUser);

      // Set auth token in API service
      const token = await authService.getAuthToken();
      if (token) {
        apiService.setAuthToken(token);
      }
    } finally {
      setActionLoading(false);
    }
  }, []);

  const createAccountWithPassword = useCallback(
    async (email: string, password: string, displayName?: string): Promise<void> => {
      setActionLoading(true);
      try {
        // Reset initial auth flag to ensure workspace clearing
        isInitialAuthRef.current = true;

        // Clear any previous workspace context before auth
        localStorage.removeItem('workspaceId');
        localStorage.removeItem('workspaceData');
        localStorage.removeItem('workspaceOwnerId');

        const authUser = await authService.createAccountWithPassword(email, password, displayName);
        setUser(authUser);

        // Set auth token in API service
        const token = await authService.getAuthToken();
        if (token) {
          apiService.setAuthToken(token);
        }
      } finally {
        setActionLoading(false);
      }
    },
    [],
  );

  const sendPasswordReset = useCallback(async (email: string): Promise<void> => {
    return authService.sendPasswordReset(email);
  }, []);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string): Promise<void> => {
      return authService.changePassword(currentPassword, newPassword);
    },
    [],
  );

  const sendEmailVerification = useCallback(async (): Promise<void> => {
    return authService.sendEmailVerification();
  }, []);

  const updateUserProfile = useCallback(
    async (updates: { displayName?: string; photoURL?: string }): Promise<void> => {
      const updatedUser = await authService.updateUserProfile(updates);
      setUser(updatedUser);
    },
    [],
  );

  // Memoised with a stable identity. refreshUser is consumed in the dependency
  // arrays of VerifyEmail's verify effect and EmailVerificationPrompt's poll
  // effect. When it was recreated every render, those effects re-ran on every
  // auth-state change — and since refreshUser itself calls setUser(), that was
  // a self-sustaining re-render loop that hammered Firebase into
  // auth/quota-exceeded. It only closes over module-stable refs (auth,
  // apiService, setUser), so an empty dep array is correct.
  const refreshUser = useCallback(async (): Promise<void> => {
    const currentUser = pageUser(auth.currentUser);
    if (currentUser) {
      // Reload user profile to get latest emailVerified status, then force a
      // token refresh AND install the new token in apiService. Without the
      // setAuthToken call, the freshly-minted token (which carries the updated
      // email_verified claim) sits unused in Firebase's local cache while
      // every outgoing request continues to send the stale Authorization
      // header — producing 403 EMAIL_VERIFICATION_REQUIRED on the first call
      // after the user verifies via the polling path.
      await currentUser.reload();
      const fresh = await currentUser.getIdToken(true);
      apiService.setAuthToken(fresh);

      // Don't call the backend until the gate would pass — avoids spurious API errors
      // while the user is sitting on the email verification prompt page
      if (!(await emailVerifiedForGate(currentUser))) {
        return;
      }

      // Optimistically flip local emailVerified BEFORE the backend round-trip.
      // The verify-email-prompt page redirects on user.emailVerified, so the
      // user must not stay stuck on that page if /auth/login is slow or fails.
      // The full user object (onboarded, internalId, etc.) is hydrated below.
      setUser((prev) =>
        prev
          ? { ...prev, emailVerified: true }
          : {
              uid: currentUser.uid,
              email: currentUser.email,
              displayName: currentUser.displayName,
              emailVerified: true,
              photoURL: currentUser.photoURL,
            },
      );

      try {
        const token = await currentUser.getIdToken();
        const response = await apiService.post('/auth/login', { idToken: token });

        if (
          response.success &&
          response.data &&
          typeof response.data === 'object' &&
          'user' in response.data
        ) {
          const backendUser = (response.data as { user: Record<string, unknown> }).user;
          // One helper for every /auth/login hydration. Four hand-rolled copies
          // of this lift is how `onboarded` came to be carried on some paths
          // and dropped on others. `internalId` is the canonical analytics
          // identity — losing it silently falls back to the Firebase UID and
          // fragments the PostHog person.
          const completeUser: AuthUser = {
            uid: currentUser.uid,
            email: currentUser.email,
            displayName: currentUser.displayName,
            emailVerified: true,
            photoURL: currentUser.photoURL,
            ...backendUserFields(
              backendUser,
              typeof backendUser.id === 'string' ? backendUser.id : undefined,
            ),
          };
          setUser(completeUser);
        }
      } catch (error) {
        console.error('Failed to refresh user state:', error);
      }
    }
  }, []);

  /**
   * Optimistically mark the local auth-context user as onboarded (and,
   * optionally, sync the display name it chose) WITHOUT a backend round-trip.
   *
   * Onboarding persists `onboarded=true` server-side via `markOnboarded()`,
   * but the auth-context `user` object is only re-hydrated by `refreshUser()`
   * — which bails at the `emailVerified` gate and, either way, runs after the
   * wizard has already navigated. That left `ProtectedRoute` (App.tsx) reading
   * a stale `onboarded=false` at the moment the wizard routes to the
   * dashboard, bouncing the user straight back into onboarding. Flipping the
   * flag locally *before* navigating closes that race for every user. It is a
   * no-op if the server call failed (callers only invoke it on success), and
   * the next real hydration overwrites it with the canonical value.
   */
  const applyOnboardingCompletion = useCallback((patch?: { displayName?: string }) => {
    setUser((prev) =>
      prev
        ? {
            ...prev,
            onboarded: true,
            ...(patch?.displayName
              ? { displayName: patch.displayName, bowerName: patch.displayName }
              : {}),
          }
        : prev,
    );
  }, []);

  // Memoised so the context value has a stable identity across renders that
  // only touch unrelated state. Every callback above is useCallback-stable, so
  // this recomputes only when auth state actually changes. This makes
  // the whole context identity-stable by construction — closing the class of
  // bug this PR fixed (an auth callback whose fresh-every-render identity feeds
  // an effect dependency array and drives a re-render loop), not just the one
  // callback (refreshUser) that triggered it.
  const value: AuthContextType = useMemo(
    () => ({
      user,
      bootLoading,
      actionLoading,
      signUp,
      signIn,
      signInWithProvider,
      signInWithProviderThenLink,
      signOut,
      getAuthToken,
      refreshUser,
      applyOnboardingCompletion,
      checkEmailMethods,
      sendPasswordReset,
      changePassword,
      sendEmailVerification,
      updateUserProfile,
      // Legacy methods (deprecated)
      createAccountWithPassword,
      signInWithPassword,
    }),
    [
      user,
      bootLoading,
      actionLoading,
      signUp,
      signIn,
      signInWithProvider,
      signInWithProviderThenLink,
      signOut,
      getAuthToken,
      refreshUser,
      applyOnboardingCompletion,
      checkEmailMethods,
      sendPasswordReset,
      changePassword,
      sendEmailVerification,
      updateUserProfile,
      createAccountWithPassword,
      signInWithPassword,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
